import {
  Cell,
  ColumnDef,
  ExpandedState,
  GroupingState,
  Header,
  RowData,
  RowSelectionState,
  SortingState,
  TableOptions,
  TableState,
  Table as TanTable,
  Row as TanTableRow,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Table as BsTable } from "react-bootstrap";
import SubmitButton from "../SubmitButton";
import ColumnSearchBox from "./ColumnSearchBox";
import { EmptyEditableTableRow } from "./EditableTableRow";
import { RowExpander, AllRowsExpander } from "./RowExpander";
import TablePager from "./TablePager";

declare module "@tanstack/react-table" {
  interface TableMeta<TData extends RowData> {
    editedRows: Record<number, TData>;
    setEditedRows: React.Dispatch<React.SetStateAction<Record<number, TData>>>;
    saveRow: (row: TData) => void;
    deleteRow: (id: number) => void;
    approveRow: (id: number) => void;
  }

  interface ColumnMeta<TData, TValue> {
    filterComponent?: React.ComponentType<{
      header: Header<TData, TValue>;
    }>;
    editableComponent?: (cell: Cell<TData, TValue>) => React.ReactNode;
    headerClassName?: string;
    className?: string;
    headerStyle?: React.CSSProperties;
    style?: React.CSSProperties;
  }
}

export const metaDefault = {
  editedRows: {},
  setEditedRows: () => null,
  saveRow: () => null,
  deleteRow: () => null,
  approveRow: () => null,
};

export function useSkipper() {
  const shouldSkipRef = useRef(true);
  const shouldSkip = shouldSkipRef.current;

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = useCallback(() => {
    shouldSkipRef.current = false;
  }, []);

  useEffect(() => {
    shouldSkipRef.current = true;
  });

  return [shouldSkip, skip] as const;
}

export function useTable<TRow>({
  state,
  defaultColumn,
  ...props
}: {
  state?: Partial<TableState>;
  defaultColumn?: Partial<ColumnDef<TRow, unknown>>;
} & Omit<TableOptions<TRow>, "getCoreRowModel">) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>(
    props.initialState?.sorting ?? [],
  );
  const [expanded, setExpanded] = React.useState<ExpandedState>(
    props.initialState?.expanded ?? {},
  );
  const [grouping, setGrouping] = React.useState<GroupingState>(
    props.initialState?.grouping ?? [],
  );

  const table = useReactTable({
    state: {
      sorting,
      rowSelection,
      expanded,
      grouping,
      ...state,
    },
    defaultColumn: {
      minSize: undefined,
      maxSize: undefined,
      size: undefined,
      ...defaultColumn,
    },
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onGroupingChange: setGrouping,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...props,
  });

  return { table, rowSelection, setRowSelection };
}

// Use function syntax below, because generics on arrow syntax don't work without hacks.

// APP-1289: a row that returns an activation callback (see `rowClickHandler`)
// becomes clickable. A whole-row click handler must defer to anything an inner
// control or the browser should own — non-primary / modified clicks (new-tab,
// middle-click), clicks already handled, clicks that land on an interactive
// element (anchor / button / input / select / role=button), and clicks that
// merely finished a text selection. Shared here so EVERY clickable-row table
// gets the same correct deferral instead of re-implementing (and drifting on)
// these guards per consumer.
export function shouldIgnoreRowClick(
  e: React.MouseEvent<HTMLTableRowElement>,
): boolean {
  if (
    e.button !== 0 ||
    e.defaultPrevented ||
    e.metaKey ||
    e.ctrlKey ||
    e.shiftKey ||
    e.altKey
  ) {
    return true;
  }
  if (
    (e.target as HTMLElement).closest(
      "a, button, input, select, [role='button']",
    )
  ) {
    return true;
  }
  // Don't hijack a click that ends a text selection.
  if (window.getSelection()?.toString()) return true;
  return false;
}

// `rowClickHandler` lets a consumer make whole rows clickable WITHOUT copying
// this cell-rendering body: it returns an activation callback for rows that
// should be clickable (e.g. navigate) and `undefined` for rows that should not
// (e.g. non-navigable sub-rows). Clickable rows get a pointer cursor and run
// the callback on a plain left-click, after `shouldIgnoreRowClick` has deferred
// to inner controls. Any canonical in-row affordance (a name <Link>, a button)
// keeps owning its own clicks — this is a mouse convenience layered on top.
function TableRow<TRow>({
  row,
  rowClickHandler,
}: {
  row: TanTableRow<TRow>;
  rowClickHandler?: (row: TanTableRow<TRow>) => (() => void) | undefined;
}) {
  const onActivate = rowClickHandler?.(row);
  const handleRowClick = onActivate
    ? (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (shouldIgnoreRowClick(e)) return;
        onActivate();
      }
    : undefined;

  return (
    <tr
      onClick={handleRowClick}
      style={onActivate ? { cursor: "pointer" } : undefined}
    >
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          id={cell.id}
          className={cell.column.columnDef.meta?.className}
          style={cell.column.columnDef.meta?.headerStyle}
        >
          {cell.getIsGrouped() ? (
            // If it's a grouped cell, add an expander and row count
            <>
              <RowExpander row={cell.row} />
              {flexRender(cell.column.columnDef.cell, cell.getContext())} (
              {row.subRows.length})
            </>
          ) : cell.getIsAggregated() ? (
            // If the cell is aggregated, use the Aggregated
            // renderer for cell
            flexRender(
              cell.column.columnDef.aggregatedCell ??
                cell.column.columnDef.cell,
              cell.getContext(),
            )
          ) : cell.getIsPlaceholder() ? null : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </td>
      ))}
    </tr>
  );
}

// APP-1710 (doc-custom-field-filtering-01a11cc6e668eb59, R14, D26, D32): an
// item the filter bar shows after the column filters. `isActive` adds it to
// the mobile filter count; the mobile "Clear" calls `onClear`.
export type FilterBarItem = {
  key: string;
  element: React.ReactNode;
  isActive: boolean;
  onClear: () => void;
};

function MobileFilterWrapper<TRow>({
  headerGroups,
  filterBarItems,
  showSearchGroup,
  showSearchButton,
  handleSearchClick,
  handleEnterKeyUp,
  isSearching,
  totalRows,
}: {
  headerGroups: ReturnType<TanTable<TRow>["getHeaderGroups"]>;
  filterBarItems: FilterBarItem[];
  showSearchGroup: boolean;
  showSearchButton: boolean;
  handleSearchClick: () => void;
  handleEnterKeyUp: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  isSearching: boolean;
  totalRows: number;
}) {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    for (const group of headerGroups) {
      for (const header of group.headers) {
        if (
          header.column.getCanFilter() &&
          header.column.getFilterValue() !== undefined
        ) {
          count++;
        }
      }
    }
    for (const item of filterBarItems) {
      if (item.isActive) count++;
    }
    return count;
  }, [headerGroups, filterBarItems]);

  if (!showSearchGroup && !showSearchButton) return null;

  const filterContent = headerGroups.map((headerGroup) =>
    headerGroup.headers.map((header) =>
      header.column.getCanFilter() ? (
        <div className="column-search-item mb-3" key={header.id}>
          <ColumnSearchBox
            header={header}
            FilterComp={header.column.columnDef.meta?.filterComponent}
          />
        </div>
      ) : null,
    ),
  );

  const filterBarItemContent = filterBarItems.map((item) => (
    <div className="column-search-item mb-3" key={item.key}>
      {item.element}
    </div>
  ));

  const searchButton = showSearchButton ? (
    <div className="column-search-item mb-3">
      <SubmitButton
        label="Search"
        size="sm"
        isSubmitting={isSearching}
        onClick={handleSearchClick}
      />
    </div>
  ) : null;

  return (
    <div className={mobileFiltersOpen ? "mobile-filters-open" : ""}>
      {/* Mobile: toggle button (hidden on desktop via CSS) */}
      <div className="mobile-filter-toggle">
        <button
          className={`mobile-filter-btn ${activeFilterCount > 0 ? "has-filters" : ""}`}
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          type="button"
        >
          <svg className="filter-icon" fill="currentColor" viewBox="0 0 16 16">
            <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z" />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="mobile-filter-badge">{activeFilterCount}</span>
          )}
        </button>
        <span className="mobile-filter-result-count">
          {totalRows.toLocaleString()} result{totalRows !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Filters: inline on desktop, collapsible panel on mobile */}
      <div className="column-search-group" onKeyUp={handleEnterKeyUp}>
        {showSearchGroup && filterContent}
        {showSearchGroup && filterBarItemContent}
        {searchButton}
      </div>

      {/* Mobile: Clear/Apply actions (hidden on desktop via CSS) */}
      {mobileFiltersOpen && (
        <div className="mobile-filter-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              // One column filter change, not one per column: each change
              // derives the filters from those of the last render, so a later
              // column's change would restore the filter an earlier one cleared.
              const headers = headerGroups.flatMap((group) => group.headers);
              const cleared = new Set(
                headers
                  .filter((header) => header.column.getCanFilter())
                  .map((header) => header.column.id),
              );
              headers[0]
                ?.getContext()
                .table.setColumnFilters((filters) =>
                  filters.filter((filter) => !cleared.has(filter.id)),
                );
              for (const item of filterBarItems) {
                item.onClear();
              }
            }}
          >
            Clear
          </Button>
          <Button
            variant="warning"
            size="sm"
            onClick={() => {
              if (showSearchButton) handleSearchClick();
              setMobileFiltersOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * R183 (APP-2226): the `div.table-responsive` React Bootstrap's `responsive`
 * draws, drawn here so it can carry attributes. While its content is wider or
 * taller than the box, the box is one tab stop with the role "region" and the
 * accessible name `name`, so a keyboard reader can reach the columns it cuts
 * off and scroll to them. While the content fits, it is a plain `div` and no
 * tab stop.
 */
function TableScrollRegion({
  name,
  children,
}: {
  name: string;
  children: React.ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  const measure = useCallback(() => {
    const box = boxRef.current;
    if (!box) {
      return;
    }
    setOverflows(
      box.scrollWidth > box.clientWidth || box.scrollHeight > box.clientHeight,
    );
  }, []);

  // The rows change the content's size, so measure after every render; the
  // state only changes when the answer does.
  useLayoutEffect(measure);

  useEffect(() => {
    const box = boxRef.current;
    window.addEventListener("resize", measure);
    // jsdom has no ResizeObserver; the browsers App supports do.
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(measure);
    if (box && observer) {
      observer.observe(box);
      Array.from(box.children).forEach((child) => observer.observe(child));
    }
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [measure]);

  return (
    <div
      ref={boxRef}
      className="table-responsive"
      {...(overflows
        ? { tabIndex: 0, role: "region", "aria-label": name }
        : {})}
    >
      {children}
    </div>
  );
}

// A stable default, so the mobile filter count memo does not recompute on
// every render of a table that passes no items.
const NO_FILTER_BAR_ITEMS: FilterBarItem[] = [];

export function Table<TRow>({
  table,
  RowComp = TableRow,
  disablePagination = false,
  onSave,
  onEdit,
  onDiscard,
  editComp,
  enableAddRemove,
  onAdd,
  onRemove,
  showSearchButton = false,
  showSearchGroup = true,
  handleSearchClick = () => null,
  isSearching = false,
  isDisableGrouping = true,
  isFixedGrouping = true,
  className,
  rowClickHandler,
  filterBarItems = NO_FILTER_BAR_ITEMS,
  pageSizeOptions,
  showPager = true,
  name,
}: {
  table: TanTable<TRow>;
  RowComp?: React.ElementType;
  disablePagination?: boolean;
  onSave?: (row: TanTableRow<TRow>) => void;
  onEdit?: (row: TanTableRow<TRow>) => void;
  onDiscard?: (row: TanTableRow<TRow>) => void;
  editComp?: (row: TanTableRow<TRow>) => React.ReactNode;
  enableAddRemove?: boolean;
  onAdd?: (row?: TanTableRow<TRow>) => void;
  onRemove?: (row: TanTableRow<TRow>) => void;
  showSearchButton?: boolean;
  showSearchGroup?: boolean;
  handleSearchClick?: () => void;
  isSearching?: boolean;
  isDisableGrouping?: boolean;
  isFixedGrouping?: boolean;
  className?: string;
  // APP-1289: makes whole rows clickable via the default row renderer — see
  // `TableRow`. Returns an activation callback for clickable rows, `undefined`
  // otherwise. Ignored by custom `RowComp`s that don't forward it.
  rowClickHandler?: (row: TanTableRow<TRow>) => (() => void) | undefined;
  // APP-1710: items the filter bar shows after the column filters. See
  // `FilterBarItem`.
  filterBarItems?: FilterBarItem[];
  // The sizes the pager offers; the pager's own list when not given.
  pageSizeOptions?: number[];
  // false draws no `TablePager` (a consumer that pages server-side draws its
  // own pager).
  showPager?: boolean;
  // R183 (APP-2226): the accessible name of the box that scrolls the table.
  // With a name, the box is one tab stop with the role "region" while the
  // table overflows it (`TableScrollRegion`); with none, the table renders in
  // React Bootstrap's own `responsive` box, which carries no attribute.
  name?: string;
}) {
  const handleEnterKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        handleSearchClick();
      }
    },
    [handleSearchClick],
  );

  const headerGroups = table
    .getHeaderGroups()
    .slice(table.getHeaderGroups().length - 1);

  const totalRows = table.getPrePaginationRowModel().rows.length;

  // The header and body rows, inside the scroll box when the table has a
  // `name`, inside React Bootstrap's own `responsive` box otherwise.
  const tableContent = (
    <>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                style={{
                  minWidth:
                    typeof header.column.columnDef.minSize === "undefined"
                      ? undefined
                      : header.getSize(),
                  width:
                    typeof header.column.columnDef.size === "undefined"
                      ? undefined
                      : header.getSize(),
                  ...header.column.columnDef.meta?.headerStyle,
                }}
                className={header.column.columnDef.meta?.headerClassName}
              >
                {header.isPlaceholder ? null : (
                  <>
                    {header.column.getCanGroup() &&
                    !isFixedGrouping &&
                    !isDisableGrouping ? (
                      // If the header can be grouped, let's add a toggle
                      <div
                        onClick={header.column.getToggleGroupingHandler()}
                        className="d-inline user-select-none pe-auto me-2"
                        role="button"
                      >
                        {header.column.getIsGrouped() ? (
                          <img
                            src="/icons/exit.svg"
                            alt="layers icon"
                            width={20}
                            title="Ungroup"
                          />
                        ) : (
                          <img
                            src="/icons/layers.svg"
                            alt="layers icon"
                            width={20}
                            title="Group"
                          />
                        )}
                      </div>
                    ) : null}
                    {!header.column.getIsGrouped() ? null : (
                      <AllRowsExpander table={table} />
                    )}
                    <div
                      onClick={header.column.getToggleSortingHandler()}
                      className={
                        header.column.getCanSort()
                          ? `th-sortable d-inline user-select-none ${
                              header.column.getIsSorted() === false
                                ? ""
                                : `th-sortable-${header.column.getIsSorted()}`
                            }`
                          : ""
                      }
                      role={header.column.getCanSort() ? "button" : ""}
                    >
                      <span>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </span>
                    </div>
                  </>
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {enableAddRemove && table.getRowModel().rows.length <= 0
          ? [<EmptyEditableTableRow key="" onAdd={onAdd} />]
          : table
              .getRowModel()
              .rows.map((row) => (
                <RowComp
                  key={row.id}
                  row={row}
                  onSave={onSave}
                  onEdit={onEdit}
                  onDiscard={onDiscard}
                  editComp={editComp}
                  enableAddRemove={enableAddRemove}
                  onAdd={onAdd}
                  onRemove={onRemove}
                  rowClickHandler={rowClickHandler}
                />
              ))}
      </tbody>
    </>
  );

  return (
    <div>
      <MobileFilterWrapper
        headerGroups={headerGroups}
        filterBarItems={filterBarItems}
        showSearchGroup={showSearchGroup}
        showSearchButton={showSearchButton}
        handleSearchClick={handleSearchClick}
        handleEnterKeyUp={handleEnterKeyUp}
        isSearching={isSearching}
        totalRows={totalRows}
      />
      {name ? (
        <TableScrollRegion name={name}>
          <BsTable className={className}>{tableContent}</BsTable>
        </TableScrollRegion>
      ) : (
        <BsTable responsive className={className}>
          {tableContent}
        </BsTable>
      )}
      {disablePagination || !showPager ? null : (
        <TablePager table={table} pageSizeOptions={pageSizeOptions} />
      )}
    </div>
  );
}
